import _ from 'lodash';
import { useEffect } from 'react';
import { HOME_SCREEN_TABLE_TYPE, TrainingPromptDisplay } from '@/homescreen/homescreen.constants';
import {
  DEFAULT_PAGE_NUMBER,
  getItemTotalForTable,
  getItemsForTable,
  getPageNumberForTable,
  getPageSizeByTable,
  getSortForTable,
  isTableLoading,
  mergeFolderTree,
  replaceFolderChildren,
} from '@/homescreen/homescreenState.utilities';
import { HOME_SCREEN_TABS } from '@/main/app.constants';
import { useRegisterPersistedStore } from '@/stores/PersistenceProvider';
import { type ActionsFactory, createStore, createStoreInstance } from '@seeqdev/utils';
import type { StoreApi } from 'zustand';
import type { HomeScreenItemType } from '@/homescreen/HomeScreenTable.organism';
import type {
  Breadcrumb,
  HomeScreenDehydratedState,
  HomeScreenFolder,
  HomeScreenSearchParams,
  HomeScreenState,
  HomeScreenTableKey,
  TabFolder,
} from '@/homescreen/homescreen.types';

const initialState: HomeScreenState = Object.freeze({
  isExact: false,
  customLinksExpanded: true,
  currentTab: HOME_SCREEN_TABS.HOME,
  previousTab: HOME_SCREEN_TABS.HOME,
  currentFolderId: '',
  tabFolders: {},
  pageNumberByTable: [],
  expandedFolderIds: [],
  folderTree: [],
  searchParams: {},
  loadingTable: [],
  itemsByTable: [],
  breadcrumbs: [],
  isLoadingBreadcrumbs: false,
  sortByTable: [],
  pageSizeByTable: [
    { key: HOME_SCREEN_TABLE_TYPE.TAB, size: 15 },
    { key: HOME_SCREEN_TABLE_TYPE.SEARCH, size: 15 },
    { key: HOME_SCREEN_TABLE_TYPE.PINNED, size: 5 },
    { key: HOME_SCREEN_TABLE_TYPE.RECENT, size: 5 },
  ],
  trainingPromptDisplay: TrainingPromptDisplay.HOME_SCREEN,
  displayedAddOnIdentifier: '',
  branchCode: null,
});

const setPageNumberInDraft = (draft: HomeScreenState, table: HomeScreenTableKey, pageNumber: number) => {
  const entry = draft.pageNumberByTable.find((item) => item.key === table);

  if (entry) {
    entry.pageNumber = pageNumber;
  } else {
    draft.pageNumberByTable.push({ key: table, pageNumber });
  }
};

export const dehydrateHomeScreenState = (state: HomeScreenState): HomeScreenDehydratedState => ({
  pageSizeByTable: state.pageSizeByTable,
  trainingPromptDisplay: state.trainingPromptDisplay,
  customLinksExpanded: state.customLinksExpanded,
});

/**
 * Merges a persisted snapshot back into `store`, taking only the dehydrated fields that are
 * actually present. Sort is cleared because workbench state saved before sort stopped being
 * dehydrated still carries a `sortByTable` key that must not be restored.
 */
export const rehydrateHomeScreenState = (
  store: Pick<StoreApi<HomeScreenState>, 'setState'>,
  dehydratedState: Partial<HomeScreenDehydratedState>,
) => {
  store.setState(
    {
      sortByTable: [],
      ..._.omitBy(
        _.pick(dehydratedState, ['pageSizeByTable', 'trainingPromptDisplay', 'customLinksExpanded']),
        _.isUndefined,
      ),
    },
    false,
  );
};

export interface HomeScreenStoreActions {
  setCurrentTab: (tab: HOME_SCREEN_TABS) => void;
  setPreviousTab: (tab: HOME_SCREEN_TABS) => void;
  /** Accepts nil so the legacy "clear the current folder" call sites keep working unchanged. */
  setCurrentFolderId: (folderId: string | null | undefined) => void;
  setTabFolder: (key: HomeScreenTableKey, folder: TabFolder) => void;
  addExpandedFolderIds: (expandedFolderIds: string[]) => void;
  removeExpandedFolderIds: (collapsedFolderIds: string[]) => void;
  mergeFolderTree: (tree?: HomeScreenFolder[]) => void;
  replaceFolderChildren: (folderId: string, subfolders: HomeScreenFolder[]) => void;
  setTableSort: (table: HomeScreenTableKey, sortProperty: string, sortAsc: boolean) => void;
  setTableLoading: (table: HomeScreenTableKey, isLoading: boolean) => void;
  /**
   * Stores the loaded page for `table`. When the page comes back empty and the user is past the
   * first page, steps back a page so they are not left looking at an empty table.
   */
  setItemsForTable: (table: HomeScreenTableKey, items: HomeScreenItemType[], totalResults?: number) => void;
  setPageNumberForTable: (table: HomeScreenTableKey, pageNumber: number) => void;
  setSearchParams: (searchParams: HomeScreenSearchParams) => void;
  setPageSizeForTable: (table: HomeScreenTableKey, size: number) => void;
  /** Clears the folder tree, its expansion state, and every loaded table. */
  clearItems: () => void;
  setBreadcrumbs: (crumbs: Breadcrumb[]) => void;
  setIsLoadingBreadcrumbs: (isLoading: boolean) => void;
  clearBreadcrumbs: () => void;
  /**
   * Clears search text and pagination when the user navigates to a different part of the home
   * screen. Deliberately narrower than `reset`: sort, page size, and the loaded folder tree survive.
   */
  resetSearchAndPagination: () => void;
  setIsExact: (isExact: boolean) => void;
  setTrainingPromptDisplay: (trainingPromptDisplay: TrainingPromptDisplay) => void;
  setDisplayedAddOnIdentifier: (displayedAddOnIdentifier: string) => void;
  setCustomLinksExpanded: (customLinksExpanded: boolean) => void;
  setBranchCode: (branchCode: string | null) => void;
  reset: (overrides?: Partial<HomeScreenState>) => void;
}

const homeScreenActionsFactory: ActionsFactory<HomeScreenState, HomeScreenStoreActions> = (set, _get, resetState) => ({
  setCurrentTab: (tab) =>
    set((draft) => {
      draft.currentTab = tab;
    }),

  setPreviousTab: (tab) =>
    set((draft) => {
      draft.previousTab = tab;
    }),

  setCurrentFolderId: (folderId) =>
    set((draft) => {
      draft.currentFolderId = folderId as string;
    }),

  setTabFolder: (key, folder) =>
    set((draft) => {
      draft.tabFolders[key] = folder;
    }),

  addExpandedFolderIds: (expandedFolderIds) =>
    set((draft) => {
      draft.expandedFolderIds = _.uniq([...draft.expandedFolderIds, ...expandedFolderIds]);
    }),

  removeExpandedFolderIds: (collapsedFolderIds) =>
    set((draft) => {
      draft.expandedFolderIds = _.without(draft.expandedFolderIds, ...collapsedFolderIds);
    }),

  mergeFolderTree: (tree) =>
    set((draft) => {
      draft.folderTree = mergeFolderTree(draft.folderTree, Array.isArray(tree) ? tree : []);
    }),

  replaceFolderChildren: (folderId, subfolders) =>
    set((draft) => {
      draft.folderTree = replaceFolderChildren(draft.folderTree, folderId, subfolders);
    }),

  setTableSort: (table, sortProperty, sortAsc) =>
    set((draft) => {
      const entry = draft.sortByTable.find((item) => item.key === table);

      if (entry) {
        entry.sortProperty = sortProperty;
        entry.sortAsc = sortAsc;
      } else {
        draft.sortByTable.push({ key: table, sortProperty, sortAsc });
      }
    }),

  setTableLoading: (table, isLoading) =>
    set((draft) => {
      const stillLoading = _.without(draft.loadingTable, table);
      draft.loadingTable = isLoading ? [...stillLoading, table] : stillLoading;
    }),

  setItemsForTable: (table, items, totalResults = 0) =>
    set((draft) => {
      const entry = draft.itemsByTable.find((item) => item.key === table);

      if (entry) {
        entry.items = items;
        entry.totalResults = totalResults;
      } else {
        draft.itemsByTable.push({ key: table, items, totalResults });
      }

      const currentPageNumber = getPageNumberForTable(draft.pageNumberByTable, table);
      if (items.length === 0 && currentPageNumber > DEFAULT_PAGE_NUMBER) {
        setPageNumberInDraft(draft, table, currentPageNumber - 1);
      }
    }),

  setPageNumberForTable: (table, pageNumber) =>
    set((draft) => {
      setPageNumberInDraft(draft, table, pageNumber);
    }),

  setSearchParams: (searchParams) =>
    set((draft) => {
      draft.searchParams = searchParams;
    }),

  setPageSizeForTable: (table, size) =>
    set((draft) => {
      const entry = draft.pageSizeByTable.find((item) => item.key === table);

      if (entry) {
        entry.size = size;
      } else {
        draft.pageSizeByTable.push({ key: table, size });
      }
    }),

  clearItems: () =>
    set((draft) => {
      draft.folderTree = [];
      draft.expandedFolderIds = [];
      draft.itemsByTable = [];
    }),

  setBreadcrumbs: (crumbs) =>
    set((draft) => {
      draft.breadcrumbs = crumbs;
    }),

  setIsLoadingBreadcrumbs: (isLoading) =>
    set((draft) => {
      draft.isLoadingBreadcrumbs = isLoading;
    }),

  clearBreadcrumbs: () =>
    set((draft) => {
      draft.breadcrumbs = [];
    }),

  resetSearchAndPagination: () =>
    set((draft) => {
      draft.pageNumberByTable = [];
      draft.searchParams = { textSearch: '' };
    }),

  setIsExact: (isExact) =>
    set((draft) => {
      draft.isExact = isExact;
    }),

  setTrainingPromptDisplay: (trainingPromptDisplay) =>
    set((draft) => {
      draft.trainingPromptDisplay = trainingPromptDisplay;
    }),

  setDisplayedAddOnIdentifier: (displayedAddOnIdentifier) =>
    set((draft) => {
      draft.displayedAddOnIdentifier = displayedAddOnIdentifier;
    }),

  setCustomLinksExpanded: (customLinksExpanded) =>
    set((draft) => {
      draft.customLinksExpanded = customLinksExpanded;
    }),

  setBranchCode: (branchCode) =>
    set((draft) => {
      draft.branchCode = branchCode;
    }),

  reset: resetState,
});

const STORE_NAME = 'HomeScreen';

const [HomeScreenStoreProvider, useHomeScreenStoreInstance, useHomeScreenStoreSelector] = createStore(
  STORE_NAME,
  initialState,
  homeScreenActionsFactory,
);

export { HomeScreenStoreProvider };

export const useHomeScreenIsExact = () => useHomeScreenStoreSelector((state) => state.isExact);
export const useHomeScreenCustomLinksExpanded = () => useHomeScreenStoreSelector((state) => state.customLinksExpanded);
export const useHomeScreenCurrentTab = () => useHomeScreenStoreSelector((state) => state.currentTab);
export const useHomeScreenPreviousTab = () => useHomeScreenStoreSelector((state) => state.previousTab);
export const useHomeScreenCurrentFolderId = () => useHomeScreenStoreSelector((state) => state.currentFolderId);
export const useHomeScreenTabFolders = () => useHomeScreenStoreSelector((state) => state.tabFolders);
export const useHomeScreenExpandedFolderIds = () => useHomeScreenStoreSelector((state) => state.expandedFolderIds);
export const useHomeScreenFolderTree = () => useHomeScreenStoreSelector((state) => state.folderTree);
export const useHomeScreenSearchParams = () => useHomeScreenStoreSelector((state) => state.searchParams);
export const useHomeScreenBreadcrumbs = () => useHomeScreenStoreSelector((state) => state.breadcrumbs);
export const useHomeScreenIsLoadingBreadcrumbs = () =>
  useHomeScreenStoreSelector((state) => state.isLoadingBreadcrumbs);
export const useHomeScreenTrainingPromptDisplay = () =>
  useHomeScreenStoreSelector((state) => state.trainingPromptDisplay);
export const useHomeScreenDisplayedAddOnIdentifier = () =>
  useHomeScreenStoreSelector((state) => state.displayedAddOnIdentifier);
export const useHomeScreenBranchCode = () => useHomeScreenStoreSelector((state) => state.branchCode);

/** Current search text, or `undefined` when the user has not searched. */
export const useHomeScreenSearchText = () => useHomeScreenStoreSelector((state) => state.searchParams.textSearch);

export const useHomeScreenPageNumberForTable = (table?: HomeScreenTableKey) =>
  useHomeScreenStoreSelector((state) => getPageNumberForTable(state.pageNumberByTable, table));
export const useHomeScreenSortForTable = (table?: HomeScreenTableKey) =>
  useHomeScreenStoreSelector((state) => getSortForTable(state.sortByTable, table));
export const useHomeScreenPageSizeForTable = (table?: HomeScreenTableKey) =>
  useHomeScreenStoreSelector((state) => getPageSizeByTable(state.pageSizeByTable, table));
export const useHomeScreenItemsForTable = (table?: HomeScreenTableKey) =>
  useHomeScreenStoreSelector((state) => getItemsForTable(state.itemsByTable, table));
export const useHomeScreenItemTotalForTable = (table?: HomeScreenTableKey) =>
  useHomeScreenStoreSelector((state) => getItemTotalForTable(state.itemsByTable, table));
export const useIsHomeScreenTableLoading = (table: HomeScreenTableKey) =>
  useHomeScreenStoreSelector((state) => isTableLoading(state.loadingTable, table));

export const useHomeScreenActions = () => useHomeScreenStoreInstance().actions;

/**
 * @deprecated This function is ONLY for legacy migration from Flux/Baobab stores.
 * DO NOT use in new code.
 */
export function legacyCreateHomeScreenStore(initialStateOverrides?: Partial<HomeScreenState>) {
  return createStoreInstance<HomeScreenState, HomeScreenStoreActions>(
    initialStateOverrides ? { ...initialState, ...initialStateOverrides } : initialState,
    homeScreenActionsFactory,
    { storeName: STORE_NAME },
  );
}

export const usePersistHomeScreenStore = () => {
  const register = useRegisterPersistedStore();
  const store = useHomeScreenStoreInstance();

  useEffect(() => {
    return register({
      storeName: 'sqHomeScreenStore',
      persistenceLevel: 'WORKBENCH',
      store,
      dehydrate: dehydrateHomeScreenState,
      rehydrate: (dehydratedState: Partial<HomeScreenDehydratedState>) =>
        rehydrateHomeScreenState(store, dehydratedState),
    });
  }, [register, store]);
};
